import {createSelector} from 'reselect';
import {pick} from 'ramda';

import type {ApplicationStoreType} from 'constants/flow/flowTypes';

import type {CustomerCurrencyType, CustomerType} from 'reducers/customer/customerType';
import {isProperty} from 'libs/utils/utils';
import {
    getPrescription,
    isSavedPrescription as isSavedPrescriptionSelector,
    isPrescriptionLoaded as isPrescriptionLoadedSelector,
} from 'features/wizardMs/selectors/prescription/prescription';

/**
 * Get customer from store
 * @param {ApplicationStoreType} store - application store
 * @returns {CustomerType} - customer
 */
export const getCustomer = (store: ApplicationStoreType): CustomerType => store.customer;

/**
 * Get customer form key
 * @param {ApplicationStoreType} store - application store
 * @returns {string} - form key
 */
export const getCustomerFormKey = createSelector(
    getCustomer,
    (customer: CustomerType): string => customer.form_key,
);

/**
 * Get customer strictPrivacy value
 * @param {ApplicationStoreType} store - application store
 * @returns {boolean} - true if customer is under strict privacy
 */
export const getCustomerStrictPrivacy = createSelector(
    getCustomer,
    ({strictPrivacy}: CustomerType): boolean => strictPrivacy,
);

/**
 * Returns a customer is logged in or not.
 * @param {ApplicationStoreType} store - application store
 * @returns {boolean} - Flag is customer logged.
 */
export const isCustomerLogged = (store: ApplicationStoreType): boolean =>
    store.customer.is_logged_in;

/**
 * Get customer currency
 * @param {ApplicationStoreType} store - application store
 * @returns {CustomerType} - customer
 */
export const getCustomerCurrency = createSelector(
    getCustomer,
    (customer: CustomerType): CustomerCurrencyType => customer.currency,
);

/**
 * Get visitor ID
 * @param {ApplicationStoreType} store - application store
 * @returns {CustomerType} - customer
 */
export const getSessionId = createSelector(
    getCustomer,
    (customer: CustomerType): CustomerCurrencyType => customer.id,
);

/**
 * Get redirection link for subscribed user
 * @param {ApplicationStoreType} store - application store
 * @returns {string} - redirection link
 */
export const getSubscribedUserRedirectionLink = (store: ApplicationStoreType): string =>
    store.joinClub.redirect;

/**
 * Get affiliate id
 * @param {CustomerStateType} store - application store
 * @returns {string} affiliate id
 */
export const getAffiliate = (store: ApplicationStoreType): string => store.customer.affilate_id;

/**
 * Get the email of an authorized user
 * @param {ApplicationStoreType} store - current store
 * @return {string} email of customer
 */
export const getAuthCustomerEmail = createSelector(
    getCustomer,
    (customer: CustomerType): string => customer.email,
);

/**
 * Is customer loaded
 * @param {ApplicationStoreType} - application store
 * @returns {boolean} - is loaded
 */
export const getIsCustomerLoaded = isProperty('customer.status', 'fulfilled');

/**
 * Takes parts of customer prescription that could be saved
 * @type {SelectorType<$Shape<PrescriptionType>>}
 */
export const getSavableCustomerPrescription = createSelector(
    getPrescription,
    pick(['pd', 'os', 'od', 'nearPD', 'multiPD', 'isMultiPD', 'comment']),
);

/**
 * Get not-logged customer status after fulfilled
 * @param {ApplicationStoreType} store - application store
 * @returns {boolean} - true if customer is not logged
 */
export const getIsCustomerNotLogged = createSelector(
    getIsCustomerLoaded,
    isCustomerLogged,
    (isCustomerLoaded, isUserLogged) => isCustomerLoaded && !isUserLogged,
);

/**
 * Get amount of customer's saved prescriptions
 * @param {ApplicationStoreType} store - application store
 * @returns {string} - number of saved prescriptions
 */
export const getAmountPrescriptions = createSelector(
    getIsCustomerNotLogged,
    isPrescriptionLoadedSelector,
    isSavedPrescriptionSelector,
    (isCustomerNotLogged, isPrescriptionLoaded, isSavedPrescription) => {
        if (isCustomerNotLogged || (isPrescriptionLoaded && !isSavedPrescription)) return '0';
        if (isPrescriptionLoaded && isSavedPrescription) return '1';
        return '';
    },
);
